"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { CircleCheck } from "lucide-react";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function FeaturedGadgetsConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-80 rounded-2xl bg-muted/20 animate-pulse border border-border/50" />)}
      </div>;
  }
  if (error || products.length === 0) {
    return null;
  }
  return <div className="mt-20">
      <h3 className="font-default text-2xl font-bold mb-8 text-center">Featured Gear</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {products.map((product, i) => <div key={product.id} data-index={i} className="bg-card/50 backdrop-blur-sm rounded-2xl p-5 border border-white/5 flex flex-col transition-all hover:bg-card hover:border-primary/30 group">
            {product.featured_image_url && <Link to={product.link} className="aspect-square mb-5 overflow-hidden rounded-xl bg-muted/30 block relative">
                <div className="absolute inset-0 bg-primary/10 opacity-0 group-hover:opacity-100 transition-opacity z-10 mix-blend-overlay"></div>
                <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              </Link>}
            <Link to={product.link} className="font-default font-semibold text-lg mb-2 text-card-foreground group-hover:text-primary transition-colors line-clamp-1">
              {product.title}
            </Link>
            <div className="text-primary font-bold text-xl mb-5">${product.price.toFixed(2)}</div>
            <div className="mt-auto">
              <AddToCartButton productId={product.id} className="w-full" buttonClassName="bg-primary text-primary-foreground w-full hover:bg-primary/90 font-semibold" text="Add to Cart" showQuantity={false} />
            </div>
          </div>)}
      </div>
    </div>;
}
export default function SmartOfficeGadgets() {
  const wp_query_params = {
    per_page: 3
  };
  const wp_query = new WP_Query(wp_query_params);
  const benefits = ["Curated selection of premium products", "Fast and reliable delivery", "Secure payment options", "Exceptional customer service", "Competitive pricing"];
  return <section id="smart-office-gadgets" className="dark bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 overflow-hidden relative">
      {}
      <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-secondary/5 rounded-full blur-[150px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {}
          <div className="relative order-2 lg:order-1">
            <div className="absolute -inset-1 bg-gradient-to-tr from-primary/20 to-transparent blur-2xl rounded-3xl z-0"></div>
            <div className="relative z-10 rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-card/30 backdrop-blur-sm">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1630779/img/ergonomic_desk_workspace.png" alt="Clean ergonomic workspace" className="w-full aspect-[4/3] object-cover" loading="lazy" />
              <div className="absolute inset-0 border border-white/5 rounded-3xl pointer-events-none"></div>
            </div>
          </div>

          {}
          <div className="flex flex-col justify-center order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6 w-fit">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
              High-Performance Minimalism
            </div>
            
            <h2 className="font-default text-4xl md:text-5xl font-bold mb-6 tracking-tight text-foreground">
              Smart Office <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Gadgets</span>
            </h2>
            
            <p className="text-muted-foreground text-lg mb-8 leading-relaxed max-w-xl">
              Elevate your daily grind with our curated selection of elite workspace hardware. Designed for tech-savvy professionals who demand reliability, comfort, and a seamless aesthetic.
            </p>
            
            <ul className="space-y-4 mb-10">
              {benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-center gap-4 group">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20 group-hover:bg-primary/20 transition-colors">
                    <CircleCheck className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-foreground/90 font-medium group-hover:text-foreground transition-colors">{benefit}</span>
                </li>)}
            </ul>
            
            <div>
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8 py-6 text-lg rounded-xl shadow-[0_0_30px_-5px_var(--color-primary)] transition-all hover:shadow-[0_0_40px_-5px_var(--color-primary)] hover:-translate-y-1">
                <Link to="#">Upgrade Your Workspace</Link>
              </Button>
            </div>
          </div>
        </div>

        {}
        <EcommerceProductsProvider wp_query={{
        per_page: 3
      }}>
          <FeaturedGadgetsConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}